import { useNavigation } from '@/hooks/useNavigation'
import { useCallback } from 'react'
import { useCanRedirectToNewSchedule } from '../../queries'
import {
  BasePath,
  BuildRoute,
  PartsPath,
  PsyBasePath,
  SpecialtySearchPath,
} from './types'
import { BASE_PATH } from './constants'
import { CONCIL_LIST } from '@/modules/Screening/pages/ContextualScreen/constants'
import { PROFESSIONAL_TYPE_MAP_BY_COUNCIL } from '@/utils/professionalTypeByCouncil'

export const useRedirectByCouncil = () => {
  const navigate = useNavigation()
  const { mutateAsync: checkRedirectToNewSchedule } =
    useCanRedirectToNewSchedule()

  const getBasePath = useCallback(
    ({
      hasSlotTime,
      professionalId,
      canRedirectToNewSchedule,
      isCRM,
      specialtyId,
    }: BasePath): string => {
      if (hasSlotTime) {
        return BASE_PATH.default.preferenceTimeSlot
      }
      if (canRedirectToNewSchedule) {
        return BASE_PATH.newSchedule.professionalSearch
      }

      if (isCRM && !professionalId && !specialtyId) {
        return BASE_PATH.default.preferenceSearch
      }

      return professionalId
        ? BASE_PATH.default.category
        : BASE_PATH.default.professionalSearch
    },
    [],
  )

  const getPsychologistBasePath = useCallback(
    ({
      canRedirectToNewSchedule,
      professionalId,
      token,
      hasSlotTime,
      showNewPsychologistSearch,
      enableScreeningFlow,
    }: PsyBasePath): string => {
      if (hasSlotTime)
        return `${BASE_PATH.default.preferenceTimeSlot}/PSICOLOGO`
      if (enableScreeningFlow) return BASE_PATH.default.screeningFlow
      if (canRedirectToNewSchedule || showNewPsychologistSearch) {
        return professionalId
          ? `${BASE_PATH.default.fullProfileCRP}/${professionalId}${
              token ? `/${token}` : ''
            }`
          : BASE_PATH.default.professionalSearchCRPV2
      }
      return professionalId
        ? `${BASE_PATH.default.oldProfileCRP}/${professionalId}${
            token ? `/${token}` : ''
          }`
        : `${BASE_PATH.default.professionalSearch}/PSICOLOGO`
    },
    [],
  )

  const buildRoute = useCallback(
    ({
      basePath,
      council,
      specialtyId,
      professionalId,
      token,
      professionalType,
      canRedirectToNewSchedule,
    }: BuildRoute): string => {
      const professionalTypeRoutePart = council
        ? PROFESSIONAL_TYPE_MAP_BY_COUNCIL[council]
        : professionalType || ''

      if (
        canRedirectToNewSchedule &&
        basePath === BASE_PATH.newSchedule.professionalSearch
      ) {
        let route = `${basePath}`
        const queryParams: string[] = []
        if (professionalId) queryParams.push(`professionalId=${professionalId}`)
        if (specialtyId) queryParams.push(`specialtyId=${specialtyId}`)
        if (professionalType)
          queryParams.push(`professionalType=${professionalType}`)
        if (token) queryParams.push(`token=${token}`)
        if (queryParams.length > 0) {
          route += `?${queryParams.join('&')}`
        }
        return route
      }

      const parts: PartsPath[] = [
        basePath,
        professionalTypeRoutePart,
        professionalId,
      ]

      if (token) {
        parts.push(token)
        if (basePath.includes('/busca') && specialtyId) {
          parts.push(specialtyId)
        }
      } else if (specialtyId) {
        parts.push(specialtyId)
      }
      return parts?.filter(Boolean)?.join('/')
    },
    [],
  )

  const getSpecialtySearchPath = useCallback(
    async (params: SpecialtySearchPath): Promise<string> => {
      const {
        council,
        specialtyId,
        hasSlotTime,
        professionalId,
        professionalType,
        token,
        showNewPsychologistSearch,
        enableScreeningFlow,
      } = params
      const isCRP = council === 'CRP' || professionalType === 'PSICOLOGO'
      const isCRM = council === 'CRM' || professionalType === 'MEDICO'

      const canRedirectToNewSchedule = await checkRedirectToNewSchedule({
        council: council || CONCIL_LIST[professionalType],
        specialtyId,
        professionalType,
        professionalId,
        originUrl: window.location.href,
      })
      if (isCRP) {
        return getPsychologistBasePath({
          canRedirectToNewSchedule,
          professionalId,
          token,
          hasSlotTime,
          showNewPsychologistSearch,
          enableScreeningFlow,
        })
      }

      const basePath = getBasePath({
        hasSlotTime,
        professionalId,
        canRedirectToNewSchedule,
        isCRM,
        specialtyId,
      })

      return buildRoute({
        basePath,
        council,
        specialtyId,
        professionalId,
        token,
        professionalType,
        canRedirectToNewSchedule,
      })
    },
    [
      checkRedirectToNewSchedule,
      getBasePath,
      buildRoute,
      getPsychologistBasePath,
    ],
  )

  const redirectToAppointmentScheduling = useCallback(
    async (params: SpecialtySearchPath) => {
      const route = await getSpecialtySearchPath(params)
      navigate(route)
    },
    [getSpecialtySearchPath, navigate],
  )

  return { redirectToAppointmentScheduling }
}
